import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { doc, getDoc, setDoc } from "firebase/firestore";

import { Col, Container, Row } from "react-bootstrap";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

import Layout from "../components/layout/layout";
import PhotoCropperInput from "../components/PhotoCropperInput";
import { D1, H1, P3 } from "../components/theme/text/Headings";
import { Button, ButtonGradient } from "../components/theme/Button";
import {
  Black,
  Blue,
  DarkGrey,
  OffWhite,
  Purple,
  White,
  White08,
} from "../components/theme/colors";
import {
  ProPicGradient,
  SecPicGradient,
} from "../components/theme/ProfilePics";

import { useAuth } from "../services/AuthContext";
import { useLoading } from "../services/LoadingContext";
import { db } from "../services/firebase.config";

// OPTIMIZED: Import centralized cache system
import dataCache from "../services/DataCache";

import { validateUsername } from "../utils/reservedUsernames";

import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
  deleteObject,
} from "firebase/storage";

const SettingsProfile = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();
  const { setLoading } = useLoading(); // Use simple loading context

  const [username, setUsername] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [photoFile, setPhotoFile] = useState(null); // file to upload
  const [photoPreview, setPhotoPreview] = useState(null); // local preview URL
  const [uploading, setUploading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [gender, setGender] = useState("");

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUserState) return;

      setLoading(true);
      try {
        // OPTIMIZED: Check centralized cache first
        let profileData = dataCache.getUserProfile(currentUserState.uid);

        if (!profileData) {
          const profileDocRef = doc(
            db,
            "public-profiles",
            currentUserState.uid
          );
          const profileDoc = await getDoc(profileDocRef);
          profileData = profileDoc.exists() ? profileDoc.data() : null;

          // OPTIMIZED: Cache in centralized system
          if (profileData) {
            dataCache.setUserProfile(currentUserState.uid, profileData);
          }
        }

        if (profileData) {
          setUsername(profileData.username || "");
          setPhotoURL(profileData.photoURL || "");
          setDisplayName(profileData.displayName || "");
          setGender(profileData.gender || "");
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [currentUserState, setLoading]);

  const handleUpdateProfile = async () => {
    if (!currentUserState) {
      alert("You need to be logged in to update your profile");
      return;
    }

    // Validate username format (spaces, reserved words, etc.)
    const validationError = validateUsername(username);
    if (validationError) {
      alert(validationError);
      return;
    }

    const profileDocRef = doc(db, "public-profiles", currentUserState.uid);
    const storage = getStorage();

    try {
      setUploading(true);
      let photoProURL = null;

      if (photoFile) {
        const picRef = ref(
          storage,
          `profile-pictures/${currentUserState.uid}/${photoFile.name}`
        );
        await uploadBytes(picRef, photoFile);
        photoProURL = await getDownloadURL(picRef);
      }

      const updatedProfile = {
        uid: currentUserState.uid,
        username: username.trim().toLowerCase(),
        displayName,
        gender,
      };

      if (photoProURL) {
        updatedProfile.photoURL = photoProURL;
      } else if (!photoFile && !photoPreview && !photoURL) {
        // User has removed their photo - delete from storage if it exists
        if (photoURL) {
          await deleteProfilePhoto();
        }
        updatedProfile.photoURL = null;
      }

      await setDoc(profileDocRef, updatedProfile, { merge: true });

      // OPTIMIZED: Update cache with new profile data
      dataCache.setUserProfile(currentUserState.uid, updatedProfile);

      setUploading(false);
      navigate("/home");
    } catch (error) {
      console.error("Error updating profile: ", error);
      setUploading(false);
    }
  };

  // Function to delete profile photo from Firebase Storage
  const deleteProfilePhoto = async () => {
    if (!currentUserState || !photoURL) return;

    const storage = getStorage();

    try {
      // Extract the file path from the URL
      // Firebase Storage URLs look like: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile?alt=media&token=...
      const urlParts = photoURL.split("/o/");
      if (urlParts.length > 1) {
        const filePath = decodeURIComponent(urlParts[1].split("?")[0]);
        const fileRef = ref(storage, filePath);
        await deleteObject(fileRef);
        console.log("Profile photo deleted from storage");
      }
    } catch (error) {
      console.error("Error deleting profile photo from storage:", error);
      // Don't throw error - we still want to update the profile even if storage deletion fails
    }
  };

  return (
    <Layout $background={Black}>
      <Container>
        <Row className="justify-content-center">
          <Col style={{ padding: "0px 50px" }}>
            <H1 className="text-align-center mt-10 mb-30" color={White}>
              Account Settings
            </H1>
            {photoPreview || photoURL ? (
              <div style={{ textAlign: "center" }}>
                <ProPicGradient
                  src={photoPreview || photoURL}
                  alt="Profile Preview"
                />
              </div>
            ) : (
              <div className="justify-content-center">
                <SecPicGradient>
                  <D1 color={White}>{displayName ? displayName[0] : "?"}</D1>
                </SecPicGradient>
              </div>
            )}

            <div className="justify-content-center">
              <PhotoCropperInput
                inputId="profile-pic-upload"
                onPhotoReady={(file, previewUrl) => {
                  setPhotoFile(file);
                  setPhotoPreview(previewUrl);
                }}
                labelStyle={{
                  border: `1px solid ${White08}`,
                }}
              />

              {(photoPreview || photoURL) && (
                <Button
                  className="mt-20"
                  width="30%"
                  $background={DarkGrey}
                  $color={OffWhite}
                  $transitionBackground="lightgrey"
                  style={{
                    padding: "7px 20px",
                    marginLeft: "10px",
                    boxShadow: "none",
                  }}
                  onClick={async () => {
                    // If there's an existing photo URL, delete it from storage
                    if (photoURL && !photoPreview) {
                      await deleteProfilePhoto();
                    }

                    setPhotoPreview(null);
                    setPhotoFile(null);
                    setPhotoURL(""); // clear current photo

                    // Update the profile immediately to remove the photoURL
                    if (currentUserState) {
                      const profileDocRef = doc(
                        db,
                        "public-profiles",
                        currentUserState.uid
                      );
                      await setDoc(
                        profileDocRef,
                        {
                          photoURL: null,
                        },
                        { merge: true }
                      );

                      // OPTIMIZED: Update cache to reflect photo removal
                      const currentCachedProfile = dataCache.getUserProfile(
                        currentUserState.uid
                      );
                      if (currentCachedProfile) {
                        dataCache.setUserProfile(currentUserState.uid, {
                          ...currentCachedProfile,
                          photoURL: null,
                        });
                      }
                    }
                  }}
                >
                  <P3>Remove</P3>
                </Button>
              )}
            </div>

            <TextField
              className="mt-40"
              fullWidth
              label="username"
              variant="outlined"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "15px",
                  color: White08,
                  background: `${DarkGrey}`,
                  border: "none !important",
                  padding: "15px 20px",
                },
                "& .MuiInputBase-input:focus": {
                  outline: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "15px",
                  color: White08,
                  border: "none !important",
                },
                "& .Mui-focused": {
                  color: White, // focus label
                  borderRadius: "20px",
                  border: "none !important",
                },
                "& .MuiOutlinedInput-root": {
                  boxShadow: "none",
                  outline: "none",
                  "& fieldset": {
                    border: "none !important",
                  },
                  "&:hover fieldset": {
                    border: "none !important",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none !important",
                  },
                },
                "& .MuiInputBase-root.Mui-focused": {
                  boxShadow: "none",
                  outline: "none",
                },
              }}
            />

            <TextField
              fullWidth
              className="mt-30"
              label="display name"
              variant="outlined"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "15px",
                  color: White08,
                  background: `${DarkGrey}`,
                  border: "none !important",
                  padding: "15px 20px",
                },
                "& .MuiInputBase-input:focus": {
                  outline: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "15px",
                  color: White08,
                  border: "none !important",
                },
                "& .Mui-focused": {
                  color: White, // focus label
                  borderRadius: "20px",
                  border: "none !important",
                },
                "& .MuiOutlinedInput-root": {
                  boxShadow: "none",
                  outline: "none",
                  "& fieldset": {
                    border: "none !important",
                  },
                  "&:hover fieldset": {
                    border: "none !important",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none !important",
                  },
                },
                "& .MuiInputBase-root.Mui-focused": {
                  boxShadow: "none",
                  outline: "none",
                },
              }}
            />

            <FormControl
              fullWidth
              className="mt-30"
              sx={{
                "& *": {
                  outline: "none !important",
                  border: "none !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
              }}
            >
              <InputLabel
                id="gender-label"
                sx={{
                  color: White08,
                  "&.Mui-focused": { color: White },
                }}
              >
                Gender
              </InputLabel>
              <Select
                labelId="gender-label"
                value={gender}
                label="Gender"
                onChange={(e) => setGender(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: `${DarkGrey}`,
                      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                      borderRadius: "10px",
                      marginTop: "5px",
                      zIndex: 9999,
                    },
                  },
                  MenuListProps: {
                    sx: {
                      backgroundColor: `${DarkGrey}`,
                      padding: "8px 0",
                      "& .MuiMenuItem-root": {
                        color: White08,
                        "&:hover": {
                          backgroundColor: `${DarkGrey}`,
                        },
                        "&.Mui-selected, &.Mui-selected:hover": {
                          backgroundColor: `${DarkGrey}`,
                          color: White,
                        },
                      },
                    },
                  },
                }}
                sx={{
                  "& *": {
                    outline: "none !important",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "15px",
                    color: White08,
                    background: `${DarkGrey}`,
                    border: "none !important",
                    padding: "15px 20px",
                    outline: "none !important",
                  },
                  "& .MuiInputBase-input:focus": {
                    outline: "none",
                  },

                  "& .Mui-focused": {
                    color: White,
                    borderRadius: "20px",
                    border: "none !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    boxShadow: "none",
                    outline: "none",
                    "& fieldset": {
                      border: "none !important",
                    },
                    "&:hover fieldset": {
                      border: "none !important",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none !important",
                    },
                  },
                  "& .MuiInputBase-root.Mui-focused": {
                    boxShadow: "none",
                    outline: "none",
                  },
                  "& .MuiSelect-root": {
                    "&:focus": {
                      outline: "none !important",
                    },
                  },
                  "& .MuiInputBase-root": {
                    "&:focus": {
                      outline: "none !important",
                    },
                    "&:focus-within": {
                      outline: "none !important",
                    },
                  },
                }}
              >
                <MenuItem sx={{ color: White08 }} value="">
                  Select Gender
                </MenuItem>
                <MenuItem sx={{ color: White08 }} value="Male">
                  Male
                </MenuItem>
                <MenuItem sx={{ color: White08 }} value="Female">
                  Female
                </MenuItem>
                <MenuItem sx={{ color: White08 }} value="Non-binary">
                  Non-binary
                </MenuItem>
                <MenuItem sx={{ color: White08 }} value="Prefer not to say">
                  Prefer not to say
                </MenuItem>
              </Select>
            </FormControl>

            <ButtonGradient
              className="mt-50"
              width="100%"
              $background={Blue}
              $transitionBackground={Purple}
              onClick={handleUpdateProfile}
            >
              {uploading ? "Uploading..." : "Save Profile"}
            </ButtonGradient>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default SettingsProfile;
